import { AgentSettings } from "./agentsettings";
import { useContext, useEffect, useRef, useState, type CSSProperties, type FormEvent, type ReactNode } from "react";
import { bridge } from "../lib/bridge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { connectPhaseLabel, connectValue, disconnectValue } from "../lib/connect.mjs";
import {
  selectUpdates,
  selectViewMode,
  useBoard,
} from "../lib/board";
import { MODES, MODE_LABEL } from "../lib/constants";
import { Twist } from "./twist";
import { GithubMark, GoogleMark, MicrosoftMark } from "./logos";
import { IdentityRows } from "./identity";
import { handle } from "./invite";
import { InPage, PageSection } from "./settings/parts";
import { KeysSection, NotifySection } from "./settings/prefs";
import { ReposPanel } from "./settings/repos";
import { TeamPanel } from "./settings/team";
import { ApprovePolicyControl, SteerPolicyControl } from "./steerpolicy";
import { RetentionControl } from "./retention";
import { buildCatalogue, formatCost, formatPair } from "../lib/model-catalogue.mjs";

function SRow({ k, v, mono }: { k: ReactNode; v: ReactNode; mono?: boolean }) {
  return (
    <div className="srow">
      <span className="k">{k}</span>
      <span className={mono ? "v mono" : "v"}>{v}</span>
    </div>
  );
}

/**
 * One collapsed row per setting, opened by the file tree's chevron.
 *
 * ⚠️ THE SUMMARY IS THE POINT, not the chevron. Andrew: "settings is ugly and
 * has too many words." Every section printed its title, its rows AND a
 * paragraph explaining each button, so the one fact a person opens Settings
 * for — what is this set to right now — was buried in prose about what the
 * setting means. A closed row carries the current value, so the common case is
 * read without opening anything, and the explanations are DELETED rather than
 * hidden: a note that only appears after a click is a note nobody reads.
 */
function SSection({
  title,
  id,
  summary,
  children,
}: {
  title: string;
  id?: string;
  summary?: ReactNode;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  // Under the Settings page a section is a heading and its body, always open.
  if (useContext(InPage)) return <PageSection title={title} id={id} summary={summary}>{children}</PageSection>;
  return (
    <div className="sset" id={id}>
      <button className="sset-head" type="button" aria-expanded={open} onClick={() => setOpen(!open)}>
        <Twist open={open} />
        <span className="sset-title">{title}</span>
        {summary ? <span className="sset-sum">{summary}</span> : null}
      </button>
      {open ? <div className="sset-body">{children}</div> : null}
    </div>
  );
}

function SNote({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return (
    <p className="snote" style={style}>
      {children}
    </p>
  );
}

const MAKE_BTN = "sbtn";

/**
 * The posture every agent this user starts gets, unless the composer changes
 * it for one run.
 *
 * ⚠️ PER USER, AND ON DISK. It is written beside the zoom in
 * ~/.zevet/config.json rather than into localStorage, because localStorage is
 * scoped to the hub origin — cleared with site data, lost when the hub moves,
 * separate in every window — and a default that decides whether an agent asks
 * before it edits must not be able to quietly revert. Andrew: "add a tab to
 * settings that saves each user's default permissions."
 *
 * ⚠️ AND IT SHOWS WHAT WAS SAVED, NOT WHAT WAS ASKED FOR. The main process
 * answers with what is now stored, so a write that did not land reads as a
 * setting that did not move — which matters more on this row than on any
 * other one in this sheet.
 */
function PermissionSection() {
  const defaultMode = useBoard((s) => s.defaultMode);
  const setDefaultMode = useBoard((s) => s.setDefaultMode);
  const [err, setErr] = useState("");
  if (!bridge.local) return null;

  return (
    <SSection title="Permissions" id="settingsPermissions" summary={MODE_LABEL[defaultMode || ""] || "Auto"}>
      <div className="sbtn-row">
        {MODES.map((m) => (
          <button
            className={MAKE_BTN}
            key={m.id}
            id={"settingsMode-" + m.id}
            type="button"
            aria-pressed={defaultMode === m.id}
            disabled={defaultMode === m.id}
            onClick={() => {
              setErr("");
              void setDefaultMode(m.id).then((r) => {
                if (!r.ok) setErr(r.error || "could not save that");
              });
            }}
          >
            {m.label}
          </button>
        ))}
      </div>
      {err ? <SNote style={{ color: "var(--bad)" }}>{err}</SNote> : null}
    </SSection>
  );
}

function GithubConnectBox({ team, onDone }: { team: string; onDone: () => void }) {
  const [state, setState] = useState<
    | { phase: "idle" }
    | { phase: "starting" }
    | { phase: "waiting"; code: string }
    | { phase: "done"; login: string }
    | { phase: "fail"; message: string }
  >({ phase: "idle" });

  function click() {
    if (state.phase === "waiting") {
      window.zevet?.githubCancel?.();
      setState({ phase: "idle" });
      return;
    }
    setState({ phase: "starting" });
    window.zevet?.githubStart?.(null, team || undefined).then((r) => {
      if (!r || !r.ok) {
        setState({ phase: "fail", message: (r && r.error) || "Could not start sign-in." });
        return;
      }
      setState({ phase: "waiting", code: r.userCode || "" });
      window.zevet?.githubWait?.().then(
        (done) => {
          if (!done || !done.ok) {
            if (done && done.cancelled) setState({ phase: "idle" });
            else setState({ phase: "fail", message: (done && done.error) || "Sign-in failed." });
            return;
          }
          setState({ phase: "done", login: done.login || "" });
          onDone();
        },
        (err) => setState({ phase: "fail", message: (err && err.message) || "Sign-in failed." }),
      );
    }, (err) => setState({ phase: "fail", message: (err && err.message) || "Could not start sign-in." }));
  }

  const label = connectPhaseLabel(state.phase, "GitHub");
  const idle = state.phase === "idle" || state.phase === "done";

  const value = connectValue(state.phase, state, "GitHub");

  return (
    <div className="srow">
      <button className={MAKE_BTN} type="button" aria-label={label} disabled={state.phase === "starting"} onClick={click}>
        {idle ? <><GithubMark /> GitHub</> : label}
      </button>
      <span className="v">{value}</span>
    </div>
  );
}

function GithubDisconnectRow({ onDone }: { onDone: () => void }) {
  const [state, setState] = useState("idle");

  function click() {
    setState("busy");
    window.zevet?.githubLogout?.().then(
      (r) => {
        if (!r || !r.ok) setState("fail");
        else {
          setState("done");
          onDone();
        }
      },
      () => setState("fail"),
    );
  }

  return (
    <div className="srow">
      <button className={MAKE_BTN} type="button" disabled={state === "busy"} onClick={click}>
        {state === "busy" ? "Disconnecting…" : state === "fail" ? "Retry" : "Disconnect GitHub"}
      </button>
      <span className="v">{disconnectValue(state)}</span>
    </div>
  );
}

function TeamSignOutRow() {
  const [state, setState] = useState<"idle" | "busy" | "fail">("idle");

  function click() {
    setState("busy");
    window.zevet?.signOutTeam?.().then(
      (r) => { if (!r || !r.ok) setState("fail"); },
      () => setState("fail"),
    );
  }

  return (
    <div className="srow">
      <button className={MAKE_BTN} type="button" disabled={state === "busy"} onClick={click}>
        {state === "busy" ? "Signing out…" : state === "fail" ? "Retry" : "Sign out"}
      </button>
      <span className="v">{state === "fail" ? "Could not sign out." : "Leave this team on this machine."}</span>
    </div>
  );
}

/** Google and Microsoft: the same hub web flow (start / wait / cancel), so one box with a provider. */
function GoogleConnectBox({ team, onDone, provider = "google" }: { team: string; onDone: () => void; provider?: "google" | "microsoft" }) {
  const name = provider === "microsoft" ? "Microsoft" : "Google";
  const api = provider === "microsoft"
    ? { start: window.zevet?.microsoftStart, wait: window.zevet?.microsoftWait, cancel: window.zevet?.microsoftCancel }
    : { start: window.zevet?.googleStart, wait: window.zevet?.googleWait, cancel: window.zevet?.googleCancel };
  const [state, setState] = useState<
    | { phase: "idle" }
    | { phase: "starting" }
    | { phase: "waiting"; url?: string }
    | { phase: "done"; login: string }
    | { phase: "fail"; message: string }
  >({ phase: "idle" });

  function click() {
    if (state.phase === "waiting") {
      api.cancel?.();
      setState({ phase: "idle" });
      return;
    }
    setState({ phase: "starting" });
    api.start?.(null, team || undefined).then((r) => {
      if (!r || !r.ok) {
        setState({ phase: "fail", message: (r && r.error) || "Could not start sign-in." });
        return;
      }
      setState({ phase: "waiting", url: r.url });
      api.wait?.().then(
        (done) => {
          if (!done || !done.ok) {
            if (done && done.cancelled) setState({ phase: "idle" });
            else setState({ phase: "fail", message: (done && done.error) || "Sign-in failed." });
            return;
          }
          setState({ phase: "done", login: done.login || "" });
          onDone();
        },
        (err) => setState({ phase: "fail", message: (err && err.message) || "Sign-in failed." }),
      );
    }, (err) => setState({ phase: "fail", message: (err && err.message) || "Could not start sign-in." }));
  }

  const label = connectPhaseLabel(state.phase, name);
  const idle = state.phase === "idle" || state.phase === "done";

  const value = connectValue(state.phase, state, name);

  return (
    <div className="srow">
      <button className={MAKE_BTN} type="button" aria-label={label} disabled={state.phase === "starting"} onClick={click}>
        {idle ? <>{provider === "microsoft" ? <MicrosoftMark /> : <GoogleMark />} {name}</> : label}
      </button>
      <span className="v">
        {value}
        {state.phase === "waiting" && state.url ? (
          <>
            {" "}
            <button
              className={MAKE_BTN}
              type="button"
              onClick={() => window.open(state.url, "_blank", "noopener,noreferrer")}
            >
              Open link
            </button>
          </>
        ) : null}
      </span>
    </div>
  );
}

function GoogleDisconnectRow({ onDone }: { onDone: () => void }) {
  const [state, setState] = useState("idle");

  function click() {
    setState("busy");
    window.zevet?.googleLogout?.().then(
      (r) => {
        if (!r || !r.ok) setState("fail");
        else {
          setState("done");
          onDone();
        }
      },
      () => setState("fail"),
    );
  }

  return (
    <div className="srow">
      <button className={MAKE_BTN} type="button" disabled={state === "busy"} onClick={click}>
        {state === "busy" ? "Disconnecting…" : state === "fail" ? "Retry" : "Disconnect Google"}
      </button>
      <span className="v">{disconnectValue(state)}</span>
    </div>
  );
}

function AccountSection() {
  const whoState = useBoard((s) => s.who.state) as unknown as Record<string, unknown> | null;
  const who = useBoard((s) => s.who);
  const refreshWhoami = useBoard((s) => s.refreshWhoami);
  useEffect(() => {
    if (!who.state) refreshWhoami();
  }, [who.state, refreshWhoami]);

  if (!whoState) {
    return (
      <SSection title="Account" summary="loading…">{null}</SSection>
    );
  }

  if (whoState.ok === false) {
    return (
      <SSection title="Account" summary="Error">
                <button className={MAKE_BTN} type="button" onClick={() => refreshWhoami()}>
          Retry
        </button>
      </SSection>
    );
  }

  const login = typeof whoState.login === "string" ? whoState.login : "";
  const shared = Boolean(whoState.shared);
  const owner = Boolean(whoState.owner);
  const people = Array.isArray(whoState.people)
    ? (whoState.people as Array<{
        login: string;
        key?: string;
        provider?: string;
        owner?: boolean;
        pending?: boolean;
        state?: string;
        invitedAt?: string | null;
        emailSentAt?: string | null;
        emailError?: string | null;
        acceptedAt?: string | null;
        lastSeen?: number | null;
      }>)
    : [];
  // The SLUG (whoami's `team`), not teamLabel: passed through to
  // githubStart/googleStart below so a reconnect from a NON-default team
  // claims an identity on THAT team, not the hub's default one. Omitted
  // (falsy), the desktop IPC layer's own default already lands on the
  // default team, so an empty string here changes nothing for it.
  const teamSlug = typeof whoState.team === "string" ? whoState.team : "";
  const githubSignIn = Boolean(whoState.githubSignIn);
  const googleSignIn = Boolean(whoState.googleSignIn);
  const microsoftSignIn = Boolean(whoState.microsoftSignIn);
  const local = Boolean(bridge.local);
  const canConnect = githubSignIn && local && Boolean(window.zevet && typeof window.zevet.githubStart === "function");
  const canConnectGoogle = googleSignIn && local && Boolean(window.zevet && typeof window.zevet.googleStart === "function");
  const canConnectMicrosoft = microsoftSignIn && local && Boolean(window.zevet && typeof window.zevet.microsoftStart === "function");
  const localSession = Boolean(bridge.cfg && bridge.cfg.session);

  /* ⚠️ NO "Signed in as" ROW. The closed section header already prints the
     login, and printing it again one line below was the sheet's oldest piece
     of noise. The three-sentence paragraph that used to sit here — team key,
     open the desktop app, this provider is unavailable — is gone for the same
     reason: a button that is not there IS the message. */
  const out: ReactNode[] = [];

  /* `shared` means "on the anonymous team token, no personal session" (hub's
     `shared: !sess`) -- a real personal session (`login` set) always has
     `shared: false`. Gating this whole block on `shared` alone meant a person
     genuinely signed in never saw a Disconnect row at all: `login` first, so
     that case and the shared-but-locally-linked one both offer it. */
  if (login || localSession) {
    if (local && window.zevet && typeof window.zevet.githubLogout === "function") {
      out.push(<GithubDisconnectRow key="disc-github" onDone={() => refreshWhoami()} />);
    }
    if (local && window.zevet && typeof window.zevet.googleLogout === "function") {
      out.push(<GoogleDisconnectRow key="disc-google" onDone={() => refreshWhoami()} />);
    }
    if (local && window.zevet && typeof window.zevet.signOutTeam === "function") {
      out.push(<TeamSignOutRow key="team-signout" />);
    }
  } else if (shared) {
    if (canConnect) out.push(<GithubConnectBox key="connect-github" team={teamSlug} onDone={() => refreshWhoami()} />);
    if (canConnectGoogle) out.push(<GoogleConnectBox key="connect-google" team={teamSlug} onDone={() => refreshWhoami()} />);
    if (canConnectMicrosoft) out.push(<GoogleConnectBox key="connect-microsoft" provider="microsoft" team={teamSlug} onDone={() => refreshWhoami()} />);
  }

  // One person, many sign-ins: what is linked to me, link another, and the
  // owner's combine. Only for a real personal session.
  const me = whoState.me as { identities: Array<{ provider: string; login: string }> } | null | undefined;
  if (login && me) {
    out.push(
      <IdentityRows
        key="identities"
        identities={me.identities}
        owner={owner}
        people={people.filter((p) => !p.pending)}
        githubSignIn={githubSignIn}
        googleSignIn={googleSignIn}
        microsoftSignIn={microsoftSignIn}
        onChanged={() => refreshWhoami()}
      />,
    );
  }

  out.push(<ReportingLine key="reporting" />);

  return (
    <SSection title="Account" summary={login ? handle(login) : "not signed in"}>
      {out}
    </SSection>
  );
}

// ---------------------------------------------------------------------------
// Model credentials (D-0NN)
// ---------------------------------------------------------------------------

type CredentialMeta = {
  id: string;
  scope: "team" | "personal";
  label: string;
  provider: string;
  kind: string;
  last4: string;
  addedBy?: string;
};

type CredentialDefault = { scope: "team" | "personal" | "auto"; id?: string } | null;

type LadderStep = { credentialId: string; untilPct: number };

/** provider+kind combinations Settings offers when adding one — the same
 *  pairs hub/server.mjs's CREDENTIAL_ENV table knows an env var for. A
 *  subscription token is personal-scope only; the hub itself refuses one at
 *  team scope regardless of what this form lets someone pick, but the form
 *  does not even offer the combination when "Team" is selected. */
const CREDENTIAL_KINDS: Array<{ provider: string; kind: string; label: string; teamOk: boolean }> = [
  { provider: "anthropic", kind: "api_key", label: "Anthropic — API key", teamOk: true },
  { provider: "anthropic", kind: "subscription_token", label: "Anthropic — subscription token", teamOk: false },
  { provider: "openai", kind: "api_key", label: "OpenAI — API key", teamOk: true },
  { provider: "meta", kind: "api_key", label: "Meta — API key", teamOk: true },
];

function credentialLine(c: CredentialMeta): string {
  const scope = c.scope === "team" ? "team" : "personal";
  const who = c.scope === "team" && c.addedBy ? `, added by @${c.addedBy}` : "";
  return `${scope} · ${c.provider}/${c.kind} · …${c.last4}${who}`;
}

function CredentialAddForm({ onAdded }: { onAdded: () => void }) {
  const [scope, setScope] = useState<"team" | "personal">("personal");
  const [kindIdx, setKindIdx] = useState(0);
  const label = useRef<HTMLInputElement>(null);
  const key = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const kinds = CREDENTIAL_KINDS.filter((k) => scope === "personal" || k.teamOk);
  const chosen = kinds[Math.min(kindIdx, kinds.length - 1)];

  function submit(ev: FormEvent) {
    ev.preventDefault();
    const k = (key.current && key.current.value.trim()) || "";
    if (!k || !window.zevet?.addCredential) return;
    setBusy(true);
    setErr("");
    window.zevet
      .addCredential({
        scope,
        label: (label.current && label.current.value.trim()) || "",
        provider: chosen.provider,
        kind: chosen.kind,
        key: k,
      })
      .then(
        (r) => {
          setBusy(false);
          if (r && r.ok) {
            if (key.current) key.current.value = "";
            if (label.current) label.current.value = "";
            onAdded();
          } else {
            setErr((r && r.error) || "Could not add that credential.");
          }
        },
        () => {
          setBusy(false);
          setErr("Could not connect.");
        },
      );
  }

  return (
    <form className="sinvite" style={{ flexWrap: "wrap", gap: "6px" }} onSubmit={submit}>
      <Select
        value={scope}
        onValueChange={(v: string | null) => {
          if (!v) return;
          setScope(v as "team" | "personal");
          setKindIdx(0);
        }}
      >
        <SelectTrigger size="sm" className="h-7 shrink-0 rounded-full border-transparent bg-foreground/[0.04] px-2 text-xs" aria-label="Scope">
          <SelectValue />
        </SelectTrigger>
        <SelectContent align="start">
          <SelectItem value="personal">Personal (this machine only)</SelectItem>
          <SelectItem value="team">Team (shared with everyone)</SelectItem>
        </SelectContent>
      </Select>
      <Select value={String(kindIdx)} onValueChange={(v: string | null) => setKindIdx(Number(v ?? 0))}>
        <SelectTrigger size="sm" className="h-7 shrink-0 rounded-full border-transparent bg-foreground/[0.04] px-2 text-xs" aria-label="Provider and kind">
          <SelectValue>{() => chosen.label}</SelectValue>
        </SelectTrigger>
        <SelectContent align="start">
          {kinds.map((k, i) => (
            <SelectItem key={`${k.provider}:${k.kind}`} value={String(i)}>
              {k.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <input className="mono" type="text" ref={label} placeholder="Label (optional)" autoComplete="off" spellCheck={false} style={{ minWidth: "120px" }} />
      <input className="mono" type="password" ref={key} placeholder="Paste the key" autoComplete="off" spellCheck={false} style={{ minWidth: "220px" }} />
      <button className={MAKE_BTN} type="submit" disabled={busy}>
        Add
      </button>
      {err ? <SNote>{err}</SNote> : null}
    </form>
  );
}

/** The Auto ladder editor. Only shown once "Auto" is the chosen default — a
 *  ladder nobody has selected is dead weight to look at. */
function LadderEditor({ credentials, ladder, onSaved }: { credentials: CredentialMeta[]; ladder: LadderStep[]; onSaved: () => void }) {
  const [rows, setRows] = useState<LadderStep[]>(ladder);
  const [busy, setBusy] = useState(false);

  function save(next: LadderStep[]) {
    setRows(next);
    setBusy(true);
    window.zevet?.setCredentialLadder?.(next).then(
      () => {
        setBusy(false);
        onSaved();
      },
      () => setBusy(false),
    );
  }

  return (
    <div style={{ marginTop: "6px" }}>
      {rows.map((r, i) => (
        <div className="srow" key={i}>
          <Select
            value={r.credentialId}
            onValueChange={(v: string | null) => save(rows.map((row, j) => (j === i ? { ...row, credentialId: v || "" } : row)))}
          >
            <SelectTrigger size="sm" className="h-7 shrink-0 rounded-full border-transparent bg-foreground/[0.04] px-2 text-xs" aria-label="Credential">
              <SelectValue placeholder="(choose a credential)" />
            </SelectTrigger>
            <SelectContent align="start">
              {credentials.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.label} (…{c.last4})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="v">
            until{" "}
            <input
              className="mono"
              type="number"
              min={0}
              max={100}
              value={r.untilPct}
              style={{ width: "56px" }}
              onChange={(ev) => save(rows.map((row, j) => (j === i ? { ...row, untilPct: Number(ev.target.value) } : row)))}
            />
            %
            <button className={MAKE_BTN} type="button" disabled={busy} onClick={() => save(rows.filter((_, j) => j !== i))}>
              Remove
            </button>
          </span>
        </div>
      ))}
      <button
        className={MAKE_BTN}
        type="button"
        disabled={busy}
        onClick={() => save([...rows, { credentialId: credentials[0]?.id || "", untilPct: 100 }])}
      >
        Add a step
      </button>
    </div>
  );
}

function CredentialsSection() {
  const available = Boolean(window.zevet && typeof window.zevet.listCredentials === "function");
  const [list, setList] = useState<CredentialMeta[] | null>(null);
  const [def, setDef] = useState<CredentialDefault>(null);
  const [ladder, setLadder] = useState<LadderStep[]>([]);
  const [err, setErr] = useState("");

  function refresh() {
    if (!available || !window.zevet?.listCredentials) return;
    window.zevet.listCredentials().then(
      (r) => {
        if (r && r.ok) {
          setList((r.credentials as CredentialMeta[]) || []);
          setDef((r.default as CredentialDefault) || null);
        } else {
          setErr((r && r.error) || "Could not load credentials.");
        }
      },
      () => setErr("Could not connect."),
    );
    window.zevet?.credentialLadder?.().then((l) => setLadder(l || []));
  }

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [available]);

  if (!available) {
    return (
      <SSection title="Model credentials" summary="desktop only">{null}</SSection>
    );
  }

  function isDefault(c: CredentialMeta) {
    return Boolean(def && def.scope === c.scope && def.id === c.id);
  }

  function chooseDefault(c: CredentialMeta | null) {
    window.zevet?.setDefaultCredential?.(c ? { scope: c.scope, id: c.id } : null).then(() => refresh());
  }

  function chooseAuto() {
    window.zevet?.setDefaultCredential?.({ scope: "auto" }).then(() => refresh());
  }

  function remove(c: CredentialMeta) {
    window.zevet?.removeCredential?.({ scope: c.scope, id: c.id }).then(() => refresh());
  }

  const rows = list || [];
  const summary = def
    ? def.scope === "auto"
      ? "auto-rotating"
      : rows.find((c) => isDefault(c))?.label || "set"
    : "not set";

  return (
    <SSection title="Model credentials" summary={summary}>
      {rows.map((c) => (
        <div className="srow" key={`${c.scope}:${c.id}`}>
          <span className="k mono">{c.label || credentialLine(c)}</span>
          <span className="v">
            {c.label ? <span className="mono">{credentialLine(c)}</span> : null}
            <label style={{ marginLeft: "8px" }}>
              <input type="radio" name="credentialDefault" checked={isDefault(c)} onChange={() => chooseDefault(c)} /> use
            </label>
            <button className={MAKE_BTN} type="button" onClick={() => remove(c)}>
              Remove
            </button>
          </span>
        </div>
      ))}
      <div className="srow">
        <span className="k">Auto-rotate</span>
        <span className="v">
          <label>
            <input type="radio" name="credentialDefault" checked={Boolean(def && def.scope === "auto")} onChange={chooseAuto} /> use the ladder below
          </label>
        </span>
      </div>
      {def && def.scope === "auto" ? <LadderEditor credentials={rows} ladder={ladder} onSaved={refresh} /> : null}
      <CredentialAddForm onAdded={refresh} />
      {err ? <SNote>{err}</SNote> : null}
    </SSection>
  );
}

/** Read-only: what this machine can run, who pays, indicative price. Settings > Agents,
 *  beside the credentials and the ladder it reads (D-083). */
function ModelsSection() {
  const agents = useBoard((s) => s.localAgents);
  const consoles = useBoard((s) => s.myConsoles);
  const [creds, setCreds] = useState<CredentialMeta[]>([]);
  const [ladder, setLadder] = useState<LadderStep[]>([]);
  useEffect(() => {
    window.zevet?.listCredentials?.().then((r) => { if (r && r.ok) setCreds((r.credentials as CredentialMeta[]) || []); }, () => {});
    window.zevet?.credentialLadder?.().then((l) => setLadder(l || []), () => {});
  }, []);
  const rows = buildCatalogue({ agents, credentials: creds, ladder, consoles });
  const row = (r: (typeof rows)[number]) => (
    <div className="srow" key={r.engine + ":" + r.id} data-model-row={r.id}>
      <span className="k">
        {r.name} <span className="mono">{r.engine}</span>
      </span>
      <span className="v mono">
        {r.available ? "ready" : "unavailable"} · {r.payers.join(", ") || "no credential"} · {formatPair(r.price)} · run {formatCost(r.runMedian)}
      </span>
    </div>
  );
  /* What needs attention is shown; the rest is one click away. A page of
     twenty-nine "ready" rows is a scroll and nothing to act on. */
  const ready = rows.filter((r) => r.available);
  return (
    <SSection title="Models" summary={`${ready.length} of ${rows.length} ready`}>
      {rows.filter((r) => !r.available).map(row)}
      {ready.length ? (
        <details>
          <summary>All</summary>
          {ready.map(row)}
          <SNote>$ per MTok in / out</SNote>
        </details>
      ) : null}
    </SSection>
  );
}

function IndexSection() {
  const stripMachine = useBoard((s) => s.strip.machine);
  const indexStatus = useBoard((s) => s.indexStatus);
  const refreshIndexStatus = useBoard((s) => s.refreshIndexStatus);
  const setIndex = useBoard((s) => s.setIndex);
  const index = useBoard((s) => s.index);
  const localRoot = useBoard((s) => s.localRoot);

  /* ⚠️ THE DEPS WERE EMPTY, and two things this reads arrive after mount.
     `localRoot` is the one that mattered: the index status is a fact about a
     FOLDER, and opening a different one never re-asked, so the Code search
     section went on describing the repo you had left. `stripMachine` is
     fetched too, so the guard above it was always evaluated against null on
     the one run this effect ever had. */
  useEffect(() => {
    if (stripMachine && stripMachine.cindex === true) return;
    if (bridge.local && typeof bridge.local.indexStatus === "function") refreshIndexStatus();
  }, [localRoot, stripMachine, refreshIndexStatus]);

  const m = stripMachine as (StatusResultView & { cindex?: boolean; cindexPort?: number }) | null;
  if (m && m.cindex === true) {
    return (
      <SSection title="Code search" summary="external">{null}</SSection>
    );
  }
  if (!bridge.local || typeof bridge.local.indexStatus !== "function") {
    return (
      <SSection title="Code search" summary="unavailable">{null}</SSection>
    );
  }
  const st = (indexStatus || null) as IndexStatusView | null;
  if (!st) {
    return (
      <SSection title="Code search" summary="checking…">{null}</SSection>
    );
  }

  const measured = st.measured || {};
  const node: ReactNode[] = [];
  node.push(
    <SRow
      key="machine"
      k="This machine"
      v={measured.totalMemMB
        ? Math.round(measured.totalMemMB / 1024) + " GB RAM \u00b7 " + measured.cores + " cores \u00b7 " + Math.round((measured.freeDiskMB || 0) / 1024) + " GB free"
        : "could not be measured"}
    />,
  );

  if (!st.capable) {
    node.push(<SRow key="status" k="Status" v="not enabled on this machine" />);
    return (
      <SSection title="Code search" summary="off">{node}</SSection>
    );
  }

  const stats = st.stats || null;
  node.push(
    <SRow
      key="model"
      k="Model"
      v={st.model.present ? "downloaded (" + Math.round(st.model.bytes / 1048576) + " MB)" : "not downloaded yet (about 86 MB)"}
    />,
  );
  node.push(
    <SRow key="index" k="Index" v={stats ? stats.files + " files" : "not built for this folder"} />,
  );
  node.push(
    <button
      key="btn"
      className={MAKE_BTN}
      type="button"
      style={{ marginTop: "10px" }}
      disabled={!localRoot || st.building}
      onClick={() => {
        setIndex({ progressText: "starting…", barPct: 0 });
        bridge.local && typeof bridge.local.indexEnable === "function" &&
          bridge.local.indexEnable(localRoot).then((r) => {
            setIndex({
              progressText: r && r.ok ? "done — " + r.indexed + " indexed, " + r.skipped + " skipped" : "failed: " + ((r && r.error) || "unknown"),
            });
            refreshIndexStatus();
          }).catch((err: unknown) => {
            setIndex({ progressText: "failed: " + (err instanceof Error ? err.message : "unknown") });
            refreshIndexStatus();
          });
      }}
    >
      {st.building ? "Building…" : stats ? "Refresh the index" : "Build the index"}
    </button>,
  );

  if (index.progressText !== "ready") node.push(<SNote key="prog">{index.progressText}</SNote>);
  if (index.barPct > 0 && index.barPct < 100) {
    node.push(
      <div className="sbar" key="bar">
        <span style={{ width: index.barPct + "%" }} />
      </div>,
    );
  }
  return (
    <SSection title="Code search" summary={stats ? stats.files + " files" : "not built"}>{node}</SSection>
  );
}

type StatusResultView = { repo?: { branch?: string; sha?: string } } & Record<string, unknown>;
type IndexStatusView = {
  ok?: boolean;
  measured?: { totalMemMB?: number; freeDiskMB?: number; cores?: number };
  capable?: boolean;
  reasons: string[];
  model: { present: boolean; bytes: number };
  stats?: { files: number; chunks: number };
  building?: boolean;
};

function VersionSection() {
  const { state: s } = useBoard(selectUpdates);
  if (!bridge.local || typeof bridge.local.updateStatus !== "function") {
    return (
      <SSection title="Version" summary="web">{null}</SSection>
    );
  }
  // Updates install themselves (desktop/idle-install.js); there is nothing to click, so Version is only the number.
  return <SSection title="Version" summary={bridge.cfg?.version || (s && s.running) || "unknown"}>{null}</SSection>;
}

/**
 * Linking with Masora (T5, docs/contracts/cross_app_context.md). The link runs
 * in the background from the moment the board opens (desktop/masora-link.js);
 * this section only reports it. Settings row idiom per commit 27d7004: the
 * closed row already says the state -- the linked host, "waiting for approval",
 * "not running" -- so nothing needs opening to read it.
 */
type MasoraLinkState = { phase: string; paired?: boolean; code?: string; error?: string };

const LINK_SUMMARY: Record<string, string> = {
  waiting: "waiting for approval",
  unreachable: "offline",
  error: "error",
};

function MasoraSection() {
  const localWorkspaces = useBoard((s) => s.localWorkspaces);
  const [cfg, setCfg] = useState<{ url: string; paired: boolean; member?: string; repos: Record<string, boolean>; chat?: boolean; runs?: boolean } | null>(null);
  const [link, setLink] = useState<MasoraLinkState | null>(null);

  function refresh() {
    window.zevet?.masoraConfig?.().then((c) => {
      if (c) setCfg(c);
    });
  }
  useEffect(() => {
    refresh();
    const poll = () =>
      window.zevet?.masoraLinkStatus?.().then((s: MasoraLinkState | undefined) => {
        if (!s) return;
        setLink((prev) => {
          // The background link just finished: re-read the config so the row
          // flips to the linked host and the Chat/repo switches appear.
          if (s.paired && !(prev && prev.paired)) refresh();
          return s;
        });
      });
    poll();
    const t = setInterval(poll, 2000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!bridge.local) return null;

  const phase = link ? link.phase : "";
  const summary = !cfg ? "loading…" : cfg.paired ? cfg.member || "Linked" : LINK_SUMMARY[phase] || "not linked";

  return (
    <SSection title="Masora" summary={summary}>
      {cfg && cfg.paired ? (
        <div className="srow">
          <button
            className={MAKE_BTN}
            type="button"
            onClick={() => window.zevet?.masoraUnpair?.().then(() => refresh())}
          >
            Unpair
          </button>
        </div>
      ) : (
        <div className="srow" id="masoraLinkRow">
          {phase === "waiting" ? (
            <button className={MAKE_BTN} type="button" onClick={() => window.zevet?.masoraLinkApprove?.()}>
              Approve
            </button>
          ) : (
            <button
              className={MAKE_BTN}
              type="button"
              onClick={() => window.zevet?.masoraLinkStart?.().then((s: MasoraLinkState | undefined) => s && setLink(s))}
            >
              Link now
            </button>
          )}
          {phase === "waiting" && link && link.code ? <span className="v mono">{link.code}</span> : null}
        </div>
      )}
      {phase === "error" && link && link.error ? <SNote style={{ color: "var(--bad)" }}>{link.error}</SNote> : null}
      {cfg && cfg.paired ? (
        <>
          {window.zevet?.masoraChatPush ? (
            <div className="srow" id="settingsChatPush">
              <span className="k">Chat</span>
              <span className="v">
                <button
                  className={MAKE_BTN}
                  type="button"
                  aria-pressed={Boolean(cfg.chat)}
                  onClick={() => window.zevet?.masoraChatPush?.(!cfg.chat).then((c) => c && setCfg(c))}
                >
                  {cfg.chat ? "Syncing" : "Off"}
                </button>
              </span>
            </div>
          ) : null}
          {window.zevet?.masoraRunsPoll ? (
            <div className="srow" id="settingsRunsPoll">
              <span className="k">Agent runs</span>
              <span className="v">
                <button
                  className={MAKE_BTN}
                  type="button"
                  aria-pressed={Boolean(cfg.runs)}
                  onClick={() => window.zevet?.masoraRunsPoll?.(!cfg.runs).then((c) => c && setCfg(c))}
                >
                  {cfg.runs ? "Running" : "Off"}
                </button>
              </span>
            </div>
          ) : null}
          {(localWorkspaces || []).map((w) => (
            <div className="srow" key={w.dir}>
              <span className="k">{w.name}</span>
              <span className="v">
                <button
                  className={MAKE_BTN}
                  type="button"
                  aria-pressed={Boolean(cfg.repos[w.dir])}
                  onClick={() =>
                    window.zevetLocal
                      ?.masoraRepoToggle?.(w.dir, !cfg.repos[w.dir])
                      .then((r) => r && r.repos && setCfg({ ...cfg, repos: r.repos }))
                  }
                >
                  {cfg.repos[w.dir] ? "Syncing" : "Off"}
                </button>
              </span>
            </div>
          ))}
        </>
      ) : null}
    </SSection>
  );
}

/**
 * The Masora family: one chip per sibling app, Install | Update | Connect |
 * Connected, and the chip is the action. State comes from desktop/family.js.
 */
type FamilyRow = {
  app: string;
  name: string;
  state: "Install" | "Update" | "Connect" | "Connected";
  version: string | null;
  running: boolean;
  member: string | null;
  lastSeen: string | null;
  page: string;
  download: string | null;
};

function FamilyCard({ row, onClose, onChange }: { row: FamilyRow; onClose: () => void; onChange: () => void }) {
  const [msg, setMsg] = useState("");
  const startVersion = useRef(row.version);
  const act = (action: string) =>
    window.zevet?.familyAct?.(row.app, action).then((r) => {
      if (!r) return;
      if (r.download) window.open(r.download, "_blank", "noopener,noreferrer");
      else if (action === "update") setMsg("Updating…");
      else if (action === "connect") setMsg(r.pairing === "no_owner" ? "Sign in to Masora" : "Connecting…");
      onChange();
    });
  // The sibling's heartbeat moved to a new version: the update is done.
  const updating = msg === "Updating…" && row.version === startVersion.current;
  return (
    <>
      <div className="sheet-back fcard-back" onClick={onClose} />
      <div className="fcard" role="dialog" aria-label={row.name}>
        <div className="sheet-head">
          <h2>{row.name}</h2>
          <button className="sheet-close" type="button" onClick={onClose}>
            Close
          </button>
        </div>
        <div className="fcard-body">
          {row.state === "Install" ? (
            <>
              <iframe className="fcard-frame" src={row.page} title={row.name} sandbox="allow-scripts allow-same-origin allow-popups" referrerPolicy="no-referrer" />
              {row.download ? (
                <button className={MAKE_BTN} type="button" onClick={() => window.open(row.download as string, "_blank", "noopener,noreferrer")}>
                  Download
                </button>
              ) : null}
            </>
          ) : null}
          {row.state === "Update" ? (
            <button className={MAKE_BTN} type="button" disabled={updating} onClick={() => act("update")}>
              {updating ? "Updating…" : row.running ? "Update" : "Download"}
            </button>
          ) : null}
          {row.state === "Connect" ? (
            <button className={MAKE_BTN} type="button" onClick={() => act("connect")}>
              Connect
            </button>
          ) : null}
          {row.state === "Connected" ? (
            <>
              {row.member ? <SRow k="Account" v={row.member} /> : null}
              {row.lastSeen ? <SRow k="Seen" v={new Date(row.lastSeen).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} /> : null}
              {row.app === "masora" ? (
                <button className={MAKE_BTN} type="button" onClick={() => window.zevet?.familyAct?.("masora", "disconnect").then(onChange)}>
                  Disconnect
                </button>
              ) : null}
            </>
          ) : null}
          {msg && row.state !== "Connected" && !updating ? <span className="snote">{msg}</span> : null}
          {updating ? <span className="snote">Updating…</span> : null}
        </div>
      </div>
    </>
  );
}

/** One red line when this machine's agents are not reaching the hub; nothing when they are. */
function ReportingLine() {
  const [problem, setProblem] = useState("");
  useEffect(() => {
    const ask = () => window.zevet?.reportingStatus?.().then((r) => setProblem((r && r.problem) || ""));
    ask();
    const t = setInterval(ask, 15000);
    return () => clearInterval(t);
  }, []);
  if (!problem) return null;
  return (
    <div className="srow" id="settingsReporting">
      <span className="k">Reporting</span>
      <span className="v" style={{ color: "var(--bad)" }}>{problem}</span>
    </div>
  );
}

function FamilySection() {
  const [rows, setRows] = useState<FamilyRow[] | null>(null);
  const [card, setCard] = useState("");
  const refresh = () => window.zevet?.familyStatus?.().then((r) => Array.isArray(r) && setRows(r as FamilyRow[]));
  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 4000);
    return () => clearInterval(t);
  }, []);
  if (!bridge.local || !window.zevet?.familyStatus) return null;
  const open = rows?.find((r) => r.app === card);
  const pending = rows?.find((r) => r.state !== "Connected");
  return (
    <SSection title="Family" id="settingsFamily" summary={!rows ? "…" : pending ? `${pending.name} · ${pending.state}` : "Connected"}>
      {(rows || []).map((r) => (
        <div className="srow" key={r.app}>
          <span className="k">{r.name}</span>
          <span className="v">
            <button className={MAKE_BTN + " fchip"} type="button" data-state={r.state} onClick={() => setCard(r.app)}>
              {r.state}
            </button>
          </span>
        </div>
      ))}
      {open ? <FamilyCard row={open} onClose={() => setCard("")} onChange={refresh} /> : null}
    </SSection>
  );
}

/**
 * Connections to external services via Masora (Linear, GitHub, Slack, Google
 * Drive, Gmail, Google Calendar, Notion, Zoom). Each provider's OAuth flow is
 * initiated by Masora's /api/oauth/{provider}/install, opened in the system
 * browser. Status is fetched from Masora's /api/sources. Zevet holds no secrets.
 */
function ConnectionsSection() {
  const [sources, setSources] = useState<Record<string, string> | null>(null);
  const [cfg, setCfg] = useState<{ paired: boolean } | null>(null);
  const [connecting, setConnecting] = useState<string | null>(null);

  const PROVIDERS = [
    { id: "linear", label: "Linear" },
    { id: "github", label: "GitHub" },
    { id: "slack", label: "Slack" },
    { id: "gdrive", label: "Google Drive" },
    { id: "gmail", label: "Gmail" },
    { id: "gcal", label: "Google Calendar" },
    { id: "notion", label: "Notion" },
    { id: "zoom", label: "Zoom" },
  ];

  function refresh() {
    window.zevet?.masoraConfig?.().then((c) => {
      if (c) setCfg(c);
    });
    if (cfg?.paired) {
      window.zevet?.masoraSources?.().then((r) => {
        if (r && r.sources) {
          const map: Record<string, string> = {};
          for (const s of r.sources) {
            map[s.kind] = s.status === "connected" ? "connected" : "reconnect";
          }
          setSources(map);
        } else if (r && r.error === "token") {
          // Token auth failed; show "—" status
          setSources({});
        }
      });
    }
  }

  useEffect(() => {
    if (!cfg) {
      window.zevet?.masoraConfig?.().then((c) => {
        if (c) setCfg(c);
      });
    }
  }, []);

  useEffect(() => {
    refresh();
    const handleFocus = () => refresh();
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg?.paired]);

  if (!cfg) return null;
  if (!cfg.paired) return null;

  if (!bridge.local) return null;

  function statusLabel(provider: string): string {
    if (!sources) return "loading…";
    if (sources[provider] === "connected") return "connected";
    if (sources[provider] === "reconnect") return "reconnect";
    return "—";
  }

  function buttonLabel(provider: string): string {
    const status = statusLabel(provider);
    return status === "connected" ? "Connected" : "Connect";
  }

  function connect(provider: string) {
    setConnecting(provider);
    window.zevet?.masoraConnect?.({ provider }).finally(() => {
      setConnecting(null);
      setTimeout(() => refresh(), 1000);
    });
  }

  return (
    <SSection title="Connections" summary={sources ? "configured" : "loading…"}>
      {PROVIDERS.map((p) => (
        <div className="srow" key={p.id}>
          <span className="k">{p.label}</span>
          <span className="v">
            <button
              className={MAKE_BTN}
              type="button"
              disabled={connecting === p.id}
              onClick={() => connect(p.id)}
            >
              {connecting === p.id ? "Opening…" : buttonLabel(p.id)}
            </button>
          </span>
        </div>
      ))}
    </SSection>
  );
}

type Tab = "account" | "repos" | "team" | "collab" | "agents" | "integrations" | "appearance";
const TABS: Array<[Tab, string]> = [
  ["account", "Account"],
  ["repos", "Repos"],
  ["team", "Team"],
  ["collab", "Collaboration"],
  ["agents", "Agents"],
  ["integrations", "Integrations"],
  ["appearance", "Appearance"],
];

function AccountPanel() {
  const hub = bridge.cfg?.hub || window.location.origin;
  return (
    <>
      <PageSection title="Connection">
        <SRow k="Address" v={hub} mono />
        <SRow k="This machine" v={bridge.cfg?.machine || "browser"} />
      </PageSection>
      <AccountSection />
      <VersionSection />
    </>
  );
}

function AppearancePanel() {
  const viewMode = useBoard(selectViewMode);
  const setView = useBoard((s) => s.setView);
  /* ⚠️ NO LIGHT/DARK TOGGLE HERE. Andrew: "that's already represented outside
     of settings." It lives in the rail, one click away. */
  return (
    <SSection title="View" summary={viewMode === "ide" ? "Files" : "Agent"}>
      <div className="sbtn-row">
        {(
          [
            ["ide", "Files"],
            ["agent", "Agent"],
          ] as const
        ).map(([id, label]) => (
          <button
            className={MAKE_BTN}
            id={"settingsView-" + id}
            key={id}
            type="button"
            aria-pressed={viewMode === id}
            disabled={viewMode === id}
            onClick={() => setView(id)}
          >
            {label}
          </button>
        ))}
      </div>
    </SSection>
  );
}

/** Settings as a page: fills the app beside the rail (see `.settings-page`). Esc and Back close it. */
export function SettingsPage() {
  const sheetOpen = useBoard((s) => s.sheetOpen);
  const closeSettings = useBoard((s) => s.closeSettings);
  const [tab, setTab] = useState<Tab>("account");

  useEffect(() => {
    if (!sheetOpen) return;
    document.getElementById("settingsClose")?.focus();
    return () => document.getElementById("settingsLink")?.focus();
  }, [sheetOpen]);

  if (!sheetOpen) return null;

  return (
    <InPage.Provider value={true}>
      <div className="settings-page" id="settingsPage" role="region" aria-label="Settings">
        <nav className="settings-nav" aria-label="Settings sections">
          <button className="settings-back" id="settingsClose" type="button" onClick={() => closeSettings()}>
            ‹ Back
          </button>
          <h2>Settings</h2>
          {TABS.map(([id, label]) => (
            <button
              key={id}
              type="button"
              data-settings-tab={id}
              aria-current={tab === id ? "page" : undefined}
              onClick={() => setTab(id)}
            >
              {label}
            </button>
          ))}
        </nav>
        <div className="settings-main">
          <div className="settings-content">
            <h1>{TABS.find(([id]) => id === tab)![1]}</h1>
            {tab === "account" ? <AccountPanel /> : null}
            {tab === "repos" ? (
              <>
                <ReposPanel />
                <IndexSection />
              </>
            ) : null}
            {tab === "team" ? (
              <>
                <TeamPanel />
                <PageSection title="Keep prompts and commands">
                  <RetentionControl />
                </PageSection>
              </>
            ) : null}
            {tab === "collab" ? (
              <>
                <PageSection title="Steering and starting agents">
                  <SteerPolicyControl />
                </PageSection>
                <PageSection title="Answering teammates' prompts">
                  <ApprovePolicyControl />
                </PageSection>
              </>
            ) : null}
            {tab === "agents" ? (
              <>
                <PermissionSection />
                <NotifySection />
                <AgentSettings />
                <CredentialsSection />
                <ModelsSection />
              </>
            ) : null}
            {tab === "integrations" ? (
              <>
                <MasoraSection />
                <FamilySection />
                <ConnectionsSection />
              </>
            ) : null}
            {tab === "appearance" ? (
              <>
                <AppearancePanel />
                <KeysSection />
              </>
            ) : null}
          </div>
        </div>
      </div>
    </InPage.Provider>
  );
}
